import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { BookHeart, Lock, Clock, Users, Infinity as InfinityIcon, Feather, ArrowRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/1509757/pexels-photo-1509757.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ALBUM = "https://images.pexels.com/photos/6274899/pexels-photo-6274899.jpeg";
const HANDS = "https://images.pexels.com/photos/167300/pexels-photo-167300.jpeg";

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/ricordi"); }, [user, navigate]);

  const features = [
    { icon: Lock, title: "Totalmente privato", text: "Solo tu decidi cosa custodire e cosa mostrare. Nulla è visibile senza il tuo permesso." },
    { icon: Clock, title: "Rilascio nel tempo", text: "Programma un ricordo per una data, un evento — «quando compirà 18 anni» — o dopo la tua scomparsa." },
    { icon: Users, title: "Tutori di fiducia", text: "Nomina persone care che potranno comunicare la tua scomparsa e sbloccare i ricordi." },
    { icon: InfinityIcon, title: "Per sempre", text: "Un libro dei ricordi che rende viva la tua essenza per i tuoi cari e i tuoi avi." },
  ];

  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookHeart className="w-7 h-7 text-primary" />
          <span className="font-serif text-2xl">Eterna</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/come-funziona" className="text-sm text-muted-foreground hover:text-[#2C2A29] transition-colors hidden sm:inline" data-testid="landing-howitworks-link">Come funziona</Link>
          <Link to="/accedi" className="text-sm text-muted-foreground hover:text-[#2C2A29] transition-colors" data-testid="landing-login-link">Accedi</Link>
          <Button onClick={() => navigate("/accedi?mode=register")} className="rounded-full bg-[#2C2A29] hover:bg-black text-white" data-testid="landing-start-btn">Inizia ora</Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-in">
          <div className="inline-flex items-center gap-2 text-sm text-primary bg-[#FBEDE7] px-4 py-1.5 rounded-full mb-6">
            <Feather className="w-4 h-4" /> Un lascito che parla al cuore
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C2A29]">
            Rendi la tua storia<br /><span className="text-primary italic">immortale</span> per chi ami.
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">
            Custodisci ricordi, lettere, foto, video e voci. Lasciali riaffiorare al momento giusto —
            un testamento emotivo per i tuoi eredi.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button onClick={() => navigate("/accedi?mode=register")} className="rounded-full h-12 px-8 bg-primary hover:bg-[#c9694f] text-white text-base" data-testid="hero-start-btn">
              Crea il tuo libro
            </Button>
            <Button onClick={() => navigate("/accedi")} variant="outline" className="rounded-full h-12 px-8 border-[#2C2A29]/20 text-base" data-testid="hero-login-btn">
              Ho già un account
            </Button>
          </div>
        </div>
        <div className="relative fade-in" style={{ animationDelay: "150ms" }}>
          <img src={HERO} alt="Famiglia al tramonto" className="rounded-3xl w-full h-[440px] object-cover shadow-xl" />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-border hidden sm:flex items-center gap-3">
            <img src={HANDS} alt="Mani" className="w-14 h-14 rounded-xl object-cover" />
            <div>
              <p className="text-sm font-medium">3 generazioni</p>
              <p className="text-xs text-muted-foreground">custodite con amore</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-border shadow-sm hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`feature-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FBEDE7] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-serif text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/come-funziona" className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all" data-testid="landing-howitworks-cta">
            Scopri come funziona, passo dopo passo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl overflow-hidden bg-[#2C2A29] text-white grid lg:grid-cols-2">
          <div className="p-10 lg:p-14 flex flex-col justify-center">
            <h2 className="font-serif text-3xl lg:text-4xl leading-tight">Le parole che conta di più dire, arrivano quando servono.</h2>
            <p className="mt-5 text-white/70 leading-relaxed">Programma ogni ricordo con istruzioni precise. I tuoi tutori se ne prenderanno cura, e i tuoi cari riceveranno un pezzo di te esattamente quando avrai deciso.</p>
            <Button onClick={() => navigate("/accedi?mode=register")} className="mt-8 rounded-full h-12 px-8 bg-primary hover:bg-[#c9694f] text-white self-start" data-testid="cta-start-btn">Comincia gratis</Button>
          </div>
          <img src={ALBUM} alt="Album di ricordi" className="w-full h-full object-cover min-h-[280px]" />
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="flex items-center gap-2"><BookHeart className="w-4 h-4 text-primary" /> Eterna</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/come-funziona" className="hover:text-[#2C2A29] transition-colors" data-testid="landing-footer-howitworks">Come funziona</Link>
            <Link to="/privacy" className="hover:text-[#2C2A29] transition-colors" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/termini" className="hover:text-[#2C2A29] transition-colors" data-testid="landing-footer-terms">Termini</Link>
            <a href="mailto:info@eterna.page" className="hover:text-[#2C2A29] transition-colors" data-testid="landing-footer-contact">info@eterna.page</a>
            <span>Fondatore: Massimiliano Costantini</span>
            <span>© 2026 Eterna</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
